import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { AiOutlineLogout } from "react-icons/ai";
import { FiHelpCircle } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setHistory } from "../../services/state/history/history";
import { purple } from "../../utils/colors";

export interface INav {
  label: string;
  icon: IconType;
  link: string;
  active: boolean;
  width: number;
  height: number;
  enabled: boolean;
  subNavs: ISubNav[];
}

export interface ISubNav {
  label: string;
  icon: IconType;
  link: string;
  active: boolean;
  width: number;
  height: number;
  enabled: boolean;
}

export interface ISidebarProps {
  navs: INav[];
  isOpen: boolean;
  onToggle: () => void;
  onLogout: () => void;
  onHelp: () => void;
}

interface INavItem {
  children: React.ReactNode;
  className?: string;
  active: boolean;
}

const NavItem: React.FC<INavItem> = ({ children, className, active }) => (
  <Flex
    direction="row"
    alignItems="center"
    className={className}
    color={active ? "white" : "rgba(255, 255, 255, 0.7)"}
    background={
      active ? "linear-gradient(to right, #7652f7, #9500ff)" : "transparent"
    }
    _hover={{
      textColor: "white",
      background: active
        ? "linear-gradient(to right, #7652f7, #9500ff)"
        : "#ffffff0d",
    }}
    boxShadow={active ? "0 4px 20px rgba(118, 82, 247, 0.4)" : "none"}
    borderRadius="12px"
    cursor="pointer"
    p={3}
    gap={3}
    my={1}
    transition="all 0.2s ease"
    fontWeight={active ? "600" : "500"}
  >
    {children}
  </Flex>
);

export const Sidebar: React.FC<ISidebarProps> = ({
  navs,
  isOpen,
  onToggle,
  onLogout,
  onHelp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const sidebarWidth = useBreakpointValue({
    base: "full",
    xl: "10%",
  });

  const onDashboardClick = (title: string, url: string) => {
    if (url.startsWith("http")) {
      // Open external links in a new tab
      window.open(url, "_blank", "noopener,noreferrer");
    }
    dispatch(setHistory(location.pathname));
    navigate(url);
    if (sidebarWidth === "full") onToggle(); // Close sidebar on mobile after navigation
  };

  const handleNavigation = (path: string) => {
    if (path.startsWith("http")) {
      // Open external links in a new tab
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      dispatch(setHistory(path));
      navigate(path);
    }
  };

  return (
    <Box
      h="full"
      className="Sidebar"
      bgColor="rgba(0, 0, 0, 0.4)"
      backdropFilter="blur(20px)"
      borderRight="1px solid rgba(255, 255, 255, 0.1)"
      p={4}
      width={sidebarWidth}
      position="fixed"
      transform={{
        base: isOpen ? "translateX(0)" : "translateX(-100%)",
        xl: "none",
      }}
      transition="transform 0.3s ease, width 0.3s ease"
      zIndex={1000}
    >
      <Flex
        direction="column"
        className="Sidebar__Container"
        justifyContent="space-between"
        height="full"
      >
        <Flex
          direction="column"
          className="Sidebar__Container--Navs"
          mt={5}
          gap={4}
        >
          <Flex
            direction="column"
            className="Sidebar__Container--Nav--subnavs"
            mt={5}
            gap={4}
          >
            {" "}
            {navs
              .filter((nav) => nav.enabled)
              .map((nav, index) => {
                if (nav.label) {
                  return (
                    <Box color="brand.500" key={index}>
                      <Flex
                        onClick={() => onDashboardClick(nav.label, nav.link)}
                      >
                        <NavItem
                          className={`Sidebar__Container--Navs-${nav.label}`}
                          active={nav.active}
                        >
                          <Icon
                            as={nav.icon as React.ElementType}
                            boxSize="24px"
                          />
                          <Text fontWeight="medium" fontSize="14px">
                            {nav.label}
                          </Text>
                        </NavItem>
                      </Flex>
                      {nav.active && (
                        <Box>
                          {nav.subNavs.map((subnav) => (
                            <Flex
                              ml={4}
                              onClick={() =>
                                onDashboardClick(subnav.label, subnav.link)
                              }
                            >
                              <NavItem
                                className={`Sidebar__Container--Nav--subnavs-${subnav.label}`}
                                active={subnav.active}
                              >
                                <Icon
                                  as={subnav.icon as React.ElementType}
                                  boxSize="24px"
                                />
                                <Text fontWeight="medium" fontSize="14px">
                                  {subnav.label}
                                </Text>
                              </NavItem>
                            </Flex>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      height="0.25vh"
                      backgroundColor="rgba(255, 255, 255, 0.2)"
                      borderRadius="md"
                    />
                  );
                }
              })}
          </Flex>
        </Flex>
        <Flex
          direction="column"
          className="Sidebar__Container--Actions"
          alignItems="start"
          gap={4}
        >
          <Button
            bgColor="transparent"
            p={0}
            w="fit-content"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
            onClick={onLogout}
          >
            <NavItem
              className="Sidebar__Container--Actions-Logout"
              active={false}
            >
              <Icon as={AiOutlineLogout as React.ElementType} />
              <Text fontWeight="medium">Logout</Text>
            </NavItem>
          </Button>
          <Button
            bgColor="transparent"
            p={0}
            w="fit-content"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
            onClick={onHelp}
          >
            <NavItem
              className="Sidebar__Container--Actions-Help"
              active={false}
            >
              <Icon as={FiHelpCircle as React.ElementType} />
              <Text fontWeight="medium">Help</Text>
            </NavItem>
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
